import { useMemo, useState } from "react";
import {
    BadgeCheck,
    ChevronRight,
    Heart,
    PackageCheck,
    Ruler,
    ShieldCheck,
    Star,
    Store,
    Truck,
    Undo2,
    WalletCards,
} from "lucide-react";
import { Link } from "react-router-dom";
import ProductDetailAccordion from "./ProductDetailAccordion";
import { buildProductDetailContent, ProductDetailProduct } from "./productDetailContent";
import "./ProductDetailCenter.css";
import {
    ExternalLink,
} from "lucide-react";
import {
    useNavigate,
} from "react-router-dom";
type Props = {
    product: ProductDetailProduct;
};

const formatPrice = (value: number) => new Intl.NumberFormat("en-IN").format(value);

const ProductDetailCenter = ({ product }: Props) => {
    const content = useMemo(() => buildProductDetailContent(product), [product]);
    const [selectedColor, setSelectedColor] = useState(0);
    const [selectedSize, setSelectedSize] = useState(content.sizes?.[0] ?? "");
    const navigate =
        useNavigate();

    const rawBrand =
        typeof product.brand === "string"
            ? product.brand
            : (product.brand as any)?.name ||
              (product.brand as any)?.brandName ||
              (product as any)?.brandName;

    const brandName =
        typeof rawBrand === "string" && rawBrand.trim()
            ? rawBrand.trim()
            : "-";

    const brandSlug =
        (product as any)?.brandSlug ||
        (product as any)?.brandStoreSlug ||
        (typeof product.brand === "object" && (product.brand as any)?.slug) ||
        (brandName !== "-"
            ? brandName
                .toLowerCase()
                .replace(
                    /[^a-z0-9]+/g,
                    "-"
                )
                .replace(
                    /^-|-$/g,
                    ""
                )
            : "");
    const discount =
        product.mrp > product.price
            ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
            : 0;

    const saving = Math.max(product.mrp - product.price, 0);

    return (
        <section className="pd-center">
            <div className="pd-primary">
                <h1 title={product.title}>{product.title}</h1>

                <p className="pd-description" title={product.description}>
                    {product.description}
                </p>

                {brandName !== "-" && brandSlug && (
                    <button
                        type="button"
                        className="product-brand-store-link"
                        onClick={() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            navigate(
                                `/store/${brandSlug}`,
                                {
                                    state: {
                                        brandName,
                                        brandSlug,
                                        brand: product.brand,
                                    },
                                }
                            );
                        }}
                    >
                        Visit the {
                            brandName
                        } Store

                        <ExternalLink
                            size={14}
                            strokeWidth={1.9}
                        />
                    </button>
                )}
                <div className="pd-rating-row">
                    <strong>{product.rating ? product.rating.toFixed(1) : "-"}</strong>

                    <span className="pd-stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                                key={star}
                                size={18}
                                strokeWidth={1.7}
                                fill={
                                    star <= Math.round(product.rating || 0)
                                        ? "currentColor"
                                        : "none"
                                }
                            />
                        ))}
                    </span>

                    <span className="pd-rating-count">
                        {product.reviews ? `${product.reviews} ratings` : "-"}
                    </span>
                </div>

                {product.badge && (
                    <div className="pd-pick">
                        <BadgeCheck size={17} />
                        <strong>{product.badge}</strong>
                        {content.pickText && <span>{content.pickText}</span>}
                    </div>
                )}

                <div className="pd-pricing">
                    <div className="pd-price-line">
                        <span className="pd-discount">
                            -{discount}%
                        </span>

                        <strong>
                            <sup>₹</sup>
                            {formatPrice(product.price)}
                        </strong>
                    </div>

                    <div className="pd-mrp">
                        M.R.P.: <del>₹{formatPrice(product.mrp)}</del>
                    </div>

                    <div className="pd-tax">
                        Inclusive of all taxes
                    </div>

                    {saving > 0 && (
                        <div className="pd-saving">
                            You save ₹{formatPrice(saving)}
                        </div>
                    )}
                </div>
            </div>

            <div className="pd-divider" />

            <section className="pd-section pd-offers-section">
                <div className="pd-section-head pd-offers-head">
                    <div>
                        <span className="pd-eyebrow">EXTRA SAVINGS</span>
                        <h2>Available offers</h2>
                        <p>Best offers available for this product.</p>
                    </div>

                    <span className="pd-offer-count">
                        {content.offers.length} offers
                    </span>
                </div>

                <div className="pd-offers-scroll">
                    <div className="pd-offers">
                        {content.offers.map((offer, index) => (
                            <article
                                className="pd-offer-card"
                                key={`${offer.title}-${index}`}
                            >
                                <div className="pd-offer-main">
                                    <div className="pd-offer-icon">
                                        {offer.icon}
                                    </div>

                                    <div className="pd-offer-copy">
                                        <div className="pd-offer-title-row">
                                            <strong>{offer.title}</strong>

                                            <span className="pd-offer-tag">
                                                {index === 0
                                                    ? "BEST"
                                                    : index === 1
                                                        ? "SAVE"
                                                        : index === 2
                                                            ? "EMI"
                                                            : "MEMBER"}
                                            </span>
                                        </div>

                                        <p>{offer.text}</p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="pd-offer-action"
                                >
                                    {offer.action}
                                    <ChevronRight
                                        size={14}
                                        strokeWidth={2}
                                    />
                                </button>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {content.colors?.length ? (
                <>
                    <div className="pd-divider" />

                    <section className="pd-section">
                        <div className="pd-option-title">
                            <h2>
                                {content.colorLabel || "Colours"}
                            </h2>

                            <span>
                                Selected:{" "}
                                <strong>
                                    {content.colors[selectedColor]?.name}
                                </strong>
                            </span>
                        </div>

                        <div className="pd-color-scroll">
                            <div className="pd-color-grid">
                                {content.colors.map((color, index) => (
                                    <button
                                        type="button"
                                        key={`${color.name}-${index}`}
                                        className={`pd-color-card ${selectedColor === index
                                            ? "active"
                                            : ""
                                            }`}
                                        onClick={() => setSelectedColor(index)}
                                    >
                                        <span className="pd-color-preview">
                                            <img
                                                src={color.image || product.image}
                                                alt={`${product.title} ${color.name}`}
                                            />

                                            <span
                                                className="pd-color-dot"
                                                style={{
                                                    background:
                                                        color.value ||
                                                        color.hex ||
                                                        "#d8e2ef",
                                                }}
                                            />
                                        </span>

                                        <span className="pd-color-content">
                                            <strong>
                                                {color.name}
                                            </strong>

                                            <small>
                                                ₹{formatPrice(product.price)}
                                            </small>
                                        </span>

                                        {selectedColor === index && (
                                            <BadgeCheck
                                                size={16}
                                                className="pd-color-check"
                                            />
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>
                </>
            ) : null}

            {content.sizes?.length ? (
                <>
                    <div className="pd-divider" />

                    <section className="pd-section">
                        <div className="pd-size-head">
                            <div>
                                <h2>
                                    {content.sizeLabel || "Sizes"}
                                </h2>

                                {selectedSize && (
                                    <span>
                                        Selected:{" "}
                                        <strong>
                                            {selectedSize}
                                        </strong>
                                    </span>
                                )}
                            </div>

                            {content.showSizeGuide && (
                                <button
                                    type="button"
                                    className="pd-size-guide"
                                >
                                    <Ruler size={16} />
                                    Size Guide
                                </button>
                            )}
                        </div>

                        <div className="pd-sizes">
                            {content.sizes.map((size) => (
                                <button
                                    type="button"
                                    key={size}
                                    className={
                                        selectedSize === size
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setSelectedSize(size)
                                    }
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </section>
                </>
            ) : null}

            <div className="pd-divider" />

            <section className="pd-section">
                <h2>Shopping policies</h2>

                <div className="pd-policies-scroll">
                    <div className="pd-policies">
                        <div className="pd-policy-item">
                            <span className="pd-policy-icon">
                                <Truck size={24} />
                            </span>

                            <strong>{content.policies.shippingTitle || "Fast Shipping"}</strong>
                            <span>{content.policies.shipping}</span>
                        </div>

                        <div className="pd-policy-item">
                            <span className="pd-policy-icon">
                                <PackageCheck size={24} />
                            </span>

                            <strong>{content.policies.deliveryTitle || "Free Delivery"}</strong>
                            <span>{content.policies.delivery}</span>
                        </div>

                        <div
                            className="pd-policy-item"
                            title={content.policies.returns}
                        >
                            <span className="pd-policy-icon">
                                <Undo2 size={24} />
                            </span>

                            <strong>{content.policies.returnsTitle || "Returns & Exchange"}</strong>
                            <span title={content.policies.returns}>
                                {(content.policies.returns || "").length > 75
                                    ? `${content.policies.returns.slice(0, 75)}...`
                                    : content.policies.returns}
                            </span>
                        </div>

                        <div className="pd-policy-item">
                            <span className="pd-policy-icon">
                                <WalletCards size={24} />
                            </span>

                            <strong>{content.policies.paymentTitle || "Pay on Delivery"}</strong>
                            <span>{content.policies.payment}</span>
                        </div>

                        <div className="pd-policy-item">
                            <span className="pd-policy-icon">
                                <ShieldCheck size={24} />
                            </span>

                            <strong>{content.policies.securityTitle || "Secure Transaction"}</strong>
                            <span>{content.policies.security || "Protected checkout"}</span>
                        </div>
                    </div>
                </div>
            </section>

            <div className="pd-divider" />

            <section className="pd-section pd-details-section">
                <div className="pd-details-title">
                    <div>
                        <span className="pd-eyebrow">
                            KNOW YOUR PRODUCT
                        </span>

                        <h2>Product Details</h2>
                    </div>

                    <Heart size={20} />
                </div>

                <ProductDetailAccordion
                    sections={content.detailSections}
                />
            </section>
        </section>
    );
};

export default ProductDetailCenter;